import { useMemo, useState } from "react";

import { ReactComponent as ArrowIcon } from "../../assets/images/vector_arrow.svg";
import tableStyles from "../../styles/components/templates/TemplatesTable.module.css";
import IconButton from "../../ui/buttons/IconButton";
import cardStyles from "../../ui/cards/TemplateCards.module.css";
import { statusInfo } from "../../utils/whatsappTemplateDraft";
import { accountNames, categoryLabel, languageLabel, numberLabel } from "./WhatsAppTemplatePanel";

import styles from "./WhatsAppTemplates.module.css";

export const TEMPLATE_COLUMNS =
  "minmax(180px, 2.2fr) 110px minmax(110px, 1fr) minmax(150px, 1.1fr) 110px 100px minmax(120px, 1fr)";
export const EXTERNAL_CREATOR_LABEL = "WhatsApp Manager";
export const UNGROUPED_LABEL = "Sin grupo";

const headerCellStyle = {
  textAlign: "center",
  color: "var(--color-text-text-primary)",
  fontSize: 14,
  fontFamily: "DM Sans",
  fontWeight: 500,
  lineHeight: "17px",
  minWidth: 0,
  whiteSpace: "nowrap",
  background: "transparent",
  border: "none",
  cursor: "pointer",
};

const SORT_VALUES = {
  name: (template) => template.name || "",
  status: (template) => statusInfo(template.status).label,
  account: (template) => accountNames(template.numbers).join(", "),
  number: (template) => (template.numbers || []).map(numberLabel).join(", "),
  language: (template) => languageLabel(template.language),
  category: (template) => categoryLabel(template.category),
  created_by: (template) => template.created_by || EXTERNAL_CREATOR_LABEL,
};

const HEADERS = [
  { key: "name", label: "Plantilla", align: "left" },
  { key: "status", label: "Estado" },
  { key: "account", label: "Cuenta" },
  { key: "number", label: "Número" },
  { key: "language", label: "Idioma" },
  { key: "category", label: "Categoría" },
  { key: "created_by", label: "Creada por" },
];

function HeaderCell({ children, column, sortKey, sortConfig, onSort, align = "center" }) {
  const isActive = sortConfig.key === sortKey;
  const directionLabel = sortConfig.direction === "asc" ? "↑" : "↓";
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      style={{ ...headerCellStyle, gridColumn: column, textAlign: align }}
    >
      {children} {isActive ? directionLabel : ""}
    </button>
  );
}

const rowKey = (template) =>
  `${template.connection?.waba_id || template.connection?.id || ""}-${template.name}-${template.language}`;

const sortRows = (rows, sortConfig) => {
  const valueOf = SORT_VALUES[sortConfig.key] || SORT_VALUES.name;
  const sorted = [...rows].sort((left, right) =>
    String(valueOf(left)).localeCompare(String(valueOf(right)), "es", { sensitivity: "base" }),
  );
  return sortConfig.direction === "asc" ? sorted : sorted.reverse();
};

export const groupSections = (rows) => {
  const grouped = new Map();
  rows.forEach((template) => {
    const name = template.group?.name || "";
    if (!grouped.has(name)) grouped.set(name, []);
    grouped.get(name).push(template);
  });
  const names = [...grouped.keys()].filter(Boolean).sort((left, right) => left.localeCompare(right, "es"));
  const sections = names.map((name) => ({ name, rows: grouped.get(name) }));
  if (grouped.has("")) sections.push({ name: names.length ? UNGROUPED_LABEL : "", rows: grouped.get("") });
  return sections;
};

function TemplateRow({ template, onOpen }) {
  const status = statusInfo(template.status);
  const open = () => onOpen?.(template);
  return (
    <div className={cardStyles.rowWrapper}>
      <div
        className={`${cardStyles.rowContent} ${cardStyles.attachmentRowContent}`}
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            open();
          }
        }}
        role="button"
        tabIndex={0}
        style={{ cursor: "pointer" }}
      >
        <div className={`${cardStyles.cell} ${styles.nameCell}`}>
          <span className={styles.name}>{template.name}</span>
          <span className={styles.excerpt}>{template.body_text}</span>
        </div>
        <div className={styles.statusCell}>
          <span className={`${styles.statusTag} ${styles[`status_${status.tone}`]}`}>{status.label}</span>
        </div>
        <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 3 }}>
          {accountNames(template.numbers).join(", ")}
        </div>
        <div className={styles.numbersCell} style={{ gridColumn: 4 }}>
          {(template.numbers || []).map((connection) => (
            <span className={cardStyles.tag} key={connection.id}>
              <span className={`${cardStyles.text} ${cardStyles.tagText}`}>{numberLabel(connection)}</span>
            </span>
          ))}
        </div>
        <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 5 }}>
          {languageLabel(template.language)}
        </div>
        <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 6 }}>
          {categoryLabel(template.category)}
        </div>
        <div className={`${cardStyles.cell} ${styles.metaCell}`} style={{ gridColumn: 7 }}>
          {template.created_by || EXTERNAL_CREATOR_LABEL}
        </div>
      </div>

      <div className={cardStyles.actionButtonWrapper}>
        <IconButton
          icon={<ArrowIcon />}
          size="md"
          iconSize={14}
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          aria-label={`Abrir plantilla ${template.name}`}
          className={cardStyles.actionButton}
        />
      </div>
    </div>
  );
}

const WhatsAppTemplatesTable = ({ rows, onOpen, showSections = true }) => {
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  const handleSort = (key) => {
    setSortConfig((previous) => ({
      key,
      direction: previous.key === key && previous.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sections = useMemo(() => {
    const grouped = showSections ? groupSections(rows) : [{ name: "", rows }];
    return grouped.map((section) => ({ ...section, rows: sortRows(section.rows, sortConfig) }));
  }, [rows, sortConfig, showSections]);

  return (
    <div className={tableStyles.templatesTableContainer}>
      <div className={tableStyles.templatesTable} style={{ "--templates-table-columns": TEMPLATE_COLUMNS }}>
        <div className={tableStyles.headerContainer}>
          <div className={tableStyles.headerBody}>
            <div className={tableStyles.headerGrid}>
              {HEADERS.map((header, index) => (
                <HeaderCell
                  key={header.key}
                  column={String(index + 1)}
                  sortKey={header.key}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                  align={header.align}
                >
                  {header.label}
                </HeaderCell>
              ))}
            </div>
          </div>
          <div className={tableStyles.headerActionSpacer} />
        </div>

        {sections.map((section) => (
          <div key={section.name || "ungrouped"} role="group" aria-label={section.name || undefined}>
            {section.name && (
              <div className={styles.groupHeader}>
                <span>{section.name}</span>
                <span className={styles.groupCount}>{section.rows.length}</span>
              </div>
            )}
            {section.rows.map((template) => (
              <TemplateRow key={rowKey(template)} template={template} onOpen={onOpen} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WhatsAppTemplatesTable;
